import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../Notification';
import { theme } from '../../theme';
import { Player, PlayerReview, ReviewReactions as ReviewReactionsType } from '../../types';
import { logger } from '../../utils/logger';
import { useResponsive } from '../../hooks/useResponsive';
import { formatDateShort, formatReviewTimestamp } from '../../utils/helpers';
import { ReviewReactions } from '../ReviewReactions';

interface PlayerReviewsModalProps {
  player: Player;
  matchId: string;
  isVisible: boolean;
  onClose: () => void;
}

export const PlayerReviewsModal: React.FC<PlayerReviewsModalProps> = ({
  player,
  matchId,
  isVisible,
  onClose
}) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { notification, showNotification, hideNotification } = useNotification();
  
  const [reviews, setReviews] = useState<PlayerReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [reactionsMap, setReactionsMap] = useState<{ [reviewId: string]: { reactions: ReviewReactionsType; totalReactions: number } }>({});

  useEffect(() => {
    if (isVisible && player) {
      fetchPlayerReviews();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, player, matchId]);

  const fetchPlayerReviews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/reviews/match/${matchId}/player/${player.id}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
        
        // Cargar reacciones para cada review
        const newReactionsMap: { [reviewId: string]: { reactions: ReviewReactionsType; totalReactions: number } } = {};
        
        for (const review of data.reviews || []) {
          try {
            const reactionsResponse = await fetch(getApiUrl(`/api/reviews/${review.id}/reactions`), {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (reactionsResponse.ok) {
              const reactionsData = await reactionsResponse.json();
              newReactionsMap[review.id] = {
                reactions: reactionsData.reactions || {},
                totalReactions: reactionsData.totalReactions || 0
              };
            }
          } catch (error) {
            logger.error(`Error fetching reactions for review ${review.id}:`, error);
          }
        }
        
        setReactionsMap(newReactionsMap);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando reviews', 'error');
      }
    } catch (error) {
      logger.error('Error fetching player reviews:', error);
      showNotification('Error de conexión', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getTagColor = (tag: string, isPositive: boolean) => {
    return isPositive ? '#00E676' : '#FF6B6B';
  };

  const handleReactionsUpdate = (reviewId: string, reactions: ReviewReactionsType, totalReactions: number) => {
    setReactionsMap(prev => ({
      ...prev,
      [reviewId]: { reactions, totalReactions }
    }));
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '32px'
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: isMobile ? '16px' : theme.spacing.lg,
        maxWidth: isMobile ? '100%' : '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: theme.colors.shadow,
        border: `2px solid ${theme.colors.primary}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.lg
        }}>
          <h2 style={{
            margin: 0,
            color: theme.colors.primary,
            fontFamily: theme.fonts.heading,
            fontSize: isMobile ? '20px' : theme.fonts.sizeTitle
          }}>
            ⭐ Reviews de {player.name}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: theme.colors.textSecondary,
              padding: '4px'
            }}
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: theme.spacing.lg }}>
            <div style={{ fontSize: '24px', animation: 'spin 1s linear infinite' }}>⚽</div>
            <p>Cargando reviews...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: theme.spacing.lg }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
            <p style={{ color: theme.colors.textSecondary }}>
              {player.name} aún no tiene reviews en este partido
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
            {reviews.map((review) => (
              <div
                key={review.id}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: theme.borderRadius.card,
                  padding: theme.spacing.md,
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: theme.spacing.sm
                }}>
                  <div>
                    {/* No mostrar reviewer.name, solo la fecha */}
                    <div style={{
                      color: theme.colors.textSecondary,
                      fontSize: theme.fonts.sizeSmall
                    }}>
                      {formatReviewTimestamp(review.createdAt)}
                    </div>
                  </div>
                </div>

                {review.comment && (
                  <div style={{
                    marginBottom: theme.spacing.sm,
                    color: theme.colors.text,
                    fontFamily: theme.fonts.body,
                    fontSize: theme.fonts.sizeBody,
                    fontStyle: 'italic'
                  }}>
                    "{review.comment}"
                  </div>
                )}

                {/* Etiquetas positivas */}
                {review.positiveTags.length > 0 && (
                  <div style={{ marginBottom: theme.spacing.sm }}>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {review.positiveTags.map((tag) => (
                        <span
                          key={tag}
                          style={{
                            backgroundColor: getTagColor(tag, true),
                            color: '#181A1B',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: theme.fonts.sizeSmall,
                            fontWeight: theme.fonts.weightBold
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Etiquetas negativas */}
                {review.negativeTags.length > 0 && (
                  <div style={{ marginBottom: theme.spacing.sm }}>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {review.negativeTags.map((tag) => (
                        <span
                          key={tag}
                          style={{
                            backgroundColor: getTagColor(tag, false),
                            color: '#FFFFFF',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: theme.fonts.sizeSmall,
                            fontWeight: theme.fonts.weightBold
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags personalizados positivos */}
                {review.customPositiveTags && review.customPositiveTags.length > 0 && (
                  <div style={{ marginBottom: theme.spacing.sm }}>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {review.customPositiveTags.map((tag) => (
                        <span
                          key={tag}
                          style={{
                            backgroundColor: '#00BFA5',
                            color: '#FFFFFF',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: theme.fonts.sizeSmall,
                            fontWeight: theme.fonts.weightBold,
                            border: '1px solid #FFFFFF'
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags personalizados negativos */}
                {review.customNegativeTags && review.customNegativeTags.length > 0 && (
                  <div style={{ marginBottom: theme.spacing.sm }}>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}>
                      {review.customNegativeTags.map((tag) => (
                        <span
                          key={tag}
                          style={{
                            backgroundColor: '#D32F2F',
                            color: '#FFFFFF',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: theme.fonts.sizeSmall,
                            fontWeight: theme.fonts.weightBold,
                            border: '1px solid #FFFFFF'
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Etiqueta personalizada (legacy) */}

                {/* Componente de reacciones */}
                <ReviewReactions
                  reviewId={review.id}
                  matchId={matchId}
                  reactions={reactionsMap[review.id]?.reactions || {}}
                  totalReactions={reactionsMap[review.id]?.totalReactions || 0}
                  onReactionsUpdate={(reactions, totalReactions) => 
                    handleReactionsUpdate(review.id, reactions, totalReactions)
                  }
                />

              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}; 